import React, { useMemo } from "react";

import colours from "../../theme/colours";
import { getEnvironmentalMetricColor } from "./environmentalMetricStyles";

const CHART_WIDTH = 96;
const CHART_HEIGHT = 44;
const CHART_PADDING_X = 4;
const CHART_PADDING_Y = 4;
const EMPTY_BAR_COLOUR = "#d7d9dd";
const BASELINE_COLOUR = colours["greyscale-1"];
const CURRENT_TIME_COLOUR = colours["greyscale-3"];

function normalizeFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function buildDefaultSlots() {
  return Array.from({ length: 24 }, (_entry, hour) => ({
    hour,
    value: null,
  }));
}

function normalizeSlots(slots = []) {
  const defaultSlots = buildDefaultSlots();

  (Array.isArray(slots) ? slots : []).forEach((slot) => {
    const hour = Number(slot?.hour);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
      return;
    }

    defaultSlots[hour] = {
      ...defaultSlots[hour],
      ...slot,
    };
  });

  return defaultSlots;
}

function calculateBarHeight(value, minimum, maximum, plotHeight) {
  if (value === null) {
    return 0;
  }

  if (minimum === maximum) {
    return Math.max(plotHeight * 0.55, 6);
  }

  const ratio = (value - minimum) / (maximum - minimum);
  return Math.max(4, ratio * plotHeight);
}

export default function EnvironmentalMetricDayChart({
  metricKey,
  timeline = null,
  currentHourOffset = null,
}) {
  const slots = useMemo(
    () => normalizeSlots(timeline?.slots),
    [timeline?.slots],
  );
  const finiteValues = useMemo(
    () =>
      slots
        .map((slot) => normalizeFiniteNumber(slot?.value))
        .filter((value) => value !== null),
    [slots],
  );

  const drawableWidth = CHART_WIDTH - CHART_PADDING_X * 2;
  const drawableHeight = CHART_HEIGHT - CHART_PADDING_Y * 2;
  const barGap = 1;
  const barWidth = (drawableWidth - barGap * (slots.length - 1)) / slots.length;
  const minimumValue = finiteValues.length ? Math.min(...finiteValues) : 0;
  const maximumValue = finiteValues.length ? Math.max(...finiteValues) : 0;
  const normalizedCurrentHourOffset = Number.isFinite(currentHourOffset)
    ? Math.min(24, Math.max(0, currentHourOffset))
    : null;
  const currentTimeX =
    normalizedCurrentHourOffset === null
      ? null
      : CHART_PADDING_X + (normalizedCurrentHourOffset / 24) * drawableWidth;

  return (
    <div className="ml-auto shrink-0">
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="h-11 w-24"
        aria-hidden="true"
        data-testid={`environmental-chart-${metricKey}`}
      >
        <line
          x1={CHART_PADDING_X}
          y1={CHART_HEIGHT - CHART_PADDING_Y}
          x2={CHART_WIDTH - CHART_PADDING_X}
          y2={CHART_HEIGHT - CHART_PADDING_Y}
          stroke={BASELINE_COLOUR}
          strokeWidth="1"
        />

        {slots.map((slot, index) => {
          const value = normalizeFiniteNumber(slot?.value);
          const barHeight = calculateBarHeight(
            value,
            minimumValue,
            maximumValue,
            drawableHeight,
          );
          const x = CHART_PADDING_X + index * (barWidth + barGap);
          const y = CHART_HEIGHT - CHART_PADDING_Y - barHeight;

          return (
            <rect
              key={slot?.hour ?? index}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx="1.5"
              fill={
                value === null
                  ? EMPTY_BAR_COLOUR
                  : getEnvironmentalMetricColor(metricKey, value)
              }
              opacity={value === null ? 0.5 : 0.95}
            />
          );
        })}

        {currentTimeX !== null ? (
          <line
            x1={currentTimeX}
            y1={CHART_PADDING_Y}
            x2={currentTimeX}
            y2={CHART_HEIGHT - CHART_PADDING_Y}
            stroke={CURRENT_TIME_COLOUR}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        ) : null}
      </svg>
    </div>
  );
}
